import { Suspense, lazy, useState, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Outlet } from 'react-router-dom';
import { IntlProvider } from 'react-intl';
import { HelmetProvider } from 'react-helmet-async';
import { QueryProvider } from './components/providers/query-provider';
import { ThemeProvider } from './components/theme-provider';
import { SystemUiThemeProvider } from './components/system-ui-theme-provider';
import { useLocale, LocaleProvider } from './i18n/useLocale';
import { getMessages } from './i18n';
import { ErrorUI } from './components/error-ui';
import { Toaster } from 'sonner';
import { Loader2 } from 'lucide-react';
import { EnrollmentConsultSidebar } from './components/shared/enrollment-consult-sidebar';
import { DashboardLayout } from './routes/dashboard';
import { RequireInstall } from './components/install/require-install';

// Layout components stay static (critical path)
import SchoolLayout from './routes/dashboard/school/layout';
import InstitutionLayout from './routes/dashboard/institution/layout';

// ---------------------------------------------------------------------------
// Loading fallback for Suspense boundaries
// ---------------------------------------------------------------------------
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helper: wrap a lazy-loaded component in Suspense with PageLoader
// ---------------------------------------------------------------------------
function lazyPage(factory: () => Promise<{ default: React.ComponentType }>) {
  const Component = lazy(factory);
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
}

const router = createBrowserRouter([
  {
    path: '/',
    element: lazyPage(() => import('./routes/_index').then(m => ({ default: m.LandingPage }))),
    errorElement: <ErrorUI />,
  },
  {
    path: '/install',
    element: lazyPage(() => import('./routes/install').then(m => ({ default: m.InstallPage }))),
    errorElement: <ErrorUI />,
  },
  {
    path: '/maintenance',
    element: lazyPage(() => import('./routes/maintenance').then(m => ({ default: m.MaintenancePage }))),
    errorElement: <ErrorUI />,
  },
  {
    // DEV ONLY —— 主题 shell 画廊（mock 鉴权，直接渲染各家族 shell；prod 下自动跳首页）
    path: '/dev/shells',
    element: lazyPage(() => import('./routes/dev/shells').then(m => ({ default: m.DevShellsPage }))),
    errorElement: <ErrorUI />,
  },
  {
    path: '/verify/certificate',
    element: lazyPage(() => import('./routes/verify/certificate').then(m => ({ default: m.CertificateVerifyPage }))),
    errorElement: <ErrorUI />,
  },
  {
    path: '/verify/certificate/:code',
    element: lazyPage(() => import('./routes/verify/certificate').then(m => ({ default: m.CertificateVerifyPage }))),
    errorElement: <ErrorUI />,
  },
  {
    path: '/verify/judge-certificate/:code',
    element: lazyPage(() =>
      import('./routes/verify/judge-certificate/$code/page').then((m) => ({ default: m.JudgeCertificateVerifyPage })),
    ),
    errorElement: <ErrorUI />,
  },
  {
    path: '/enrollment-instruction',
    element: lazyPage(() => import('./routes/enrollment-instruction/page').then(m => ({ default: m.EnrollmentInstructionPage }))),
    errorElement: <ErrorUI />,
  },
  {
    path: '/enrollment-instruction/:id',
    element: lazyPage(() => import('./routes/enrollment-instruction/$id/page').then(m => ({ default: m.EnrollmentInstructionDetailPage }))),
    errorElement: <ErrorUI />,
  },
  {
    path: '/terms',
    element: lazyPage(() => import('./routes/terms').then(m => ({ default: m.TermsPage }))),
    errorElement: <ErrorUI />,
  },
  {
    path: '/privacy',
    element: lazyPage(() => import('./routes/privacy').then(m => ({ default: m.PrivacyPage }))),
    errorElement: <ErrorUI />,
  },
  {
    path: '/auth/magic-link/:token',
    element: lazyPage(() => import('./routes/auth/magic-link/$token/page')),
    errorElement: <ErrorUI />,
  },
  {
    path: '/invite/:code',
    element: lazyPage(() => import('./routes/invite/$code/page')),
    errorElement: <ErrorUI />,
  },
  {
    path: '/join/:code',
    element: lazyPage(() => import('./routes/join/$code/page')),
    errorElement: <ErrorUI />,
  },
  {
    element: <RequireInstall />,
    errorElement: <ErrorUI />,
    children: [
      {
        path: '/login',
        element: lazyPage(() => import('./routes/login').then(m => ({ default: m.LoginPage }))),
      },
      {
        path: '/register',
        element: lazyPage(() => import('./routes/register').then(m => ({ default: m.RegisterPage }))),
      },
      {
        path: '/role-select',
        element: lazyPage(() => import('./routes/role-select')),
      },
      {
        path: '/profile-setup',
        element: lazyPage(() => import('./routes/profile-setup').then(m => ({ default: m.ProfileSetupPage }))),
      },
      {
        path: '/name-setup',
        element: lazyPage(() => import('./routes/name-setup').then(m => ({ default: m.NameSetupPage }))),
      },
      {
        path: '/dashboard',
        element: <DashboardLayout />,
        children: [
          { index: true, element: lazyPage(() => import('./routes/dashboard/_index').then(m => ({ default: m.DashboardIndexPage }))) },
          { path: 'events', element: lazyPage(() => import('./routes/dashboard/events').then(m => ({ default: m.EventsPage }))) },
          { path: 'events/create', element: lazyPage(() => import('./routes/dashboard/events/create/page')) },
          {
            path: 'events/:eventId',
            element: <Outlet />,
            errorElement: <ErrorUI />,
            children: [
              { index: true, element: lazyPage(() => import('./routes/dashboard/events/$id/page')) },
              { path: 'enroll', element: lazyPage(() => import('./routes/dashboard/events/$id/enroll/page')) },
              { path: 'judges', element: lazyPage(() => import('./routes/dashboard/events/$id/judges/page')) },
              { path: 'certificates', element: lazyPage(() => import('./routes/dashboard/events/$id/certificates/page')) },
              { path: 'competitions/create', element: lazyPage(() => import('./routes/dashboard/competitions/create/page')) },
              { path: 'competitions/:competitionId', element: lazyPage(() => import('./routes/dashboard/competitions/$id/page')) },
              { path: 'competitions/:competitionId/settings', element: lazyPage(() => import('./routes/dashboard/competitions/$id/settings/page')) },
              { path: 'competitions/:competitionId/badges-upload', element: lazyPage(() => import('./routes/dashboard/competitions/$id/badges-upload/page')) },
              { path: 'competitions/:competitionId/stages/create', element: lazyPage(() => import('./routes/dashboard/stages/create/page')) },
              { path: 'competitions/:competitionId/stages/:stageId', element: lazyPage(() => import('./routes/dashboard/stages/$id/page')) },
              { path: 'competitions/:competitionId/stages/:stageId/edit', element: lazyPage(() => import('./routes/dashboard/stages/$id/edit/page')) },
              { path: 'competitions/:competitionId/teams/create', element: lazyPage(() => import('./routes/dashboard/teams/create/page')) },
              { path: 'competitions/:competitionId/teams/:teamId', element: lazyPage(() => import('./routes/dashboard/teams/$id/page')) },
              { path: 'competitions/:competitionId/teams/:teamId/edit', element: lazyPage(() => import('./routes/dashboard/teams/$id/edit/page')) },
              { path: 'competitions/:competitionId/score-forms/create', element: lazyPage(() => import('./routes/dashboard/score-forms/create/page')) },
              { path: 'competitions/:competitionId/score-forms/:scoreFormId', element: lazyPage(() => import('./routes/dashboard/score-forms/$id/page')) },
              { path: 'competitions/:competitionId/score-forms/:scoreFormId/edit', element: lazyPage(() => import('./routes/dashboard/score-forms/$id/edit/page')) },
            ],
          },
          { path: 'teams', element: lazyPage(() => import('./routes/dashboard/teams').then(m => ({ default: m.TeamsPage }))) },
          { path: 'profiles', element: lazyPage(() => import('./routes/dashboard/profiles').then(m => ({ default: m.ProfilesPage }))) },
          { path: 'school-no-binding', element: lazyPage(() => import('./routes/dashboard/school-no-binding').then(m => ({ default: m.SchoolNoBindingPage }))) },
          {
            path: 'school',
            element: <SchoolLayout />,
            children: [
              { index: true, element: lazyPage(() => import('./routes/dashboard/school/page')) },
              { path: 'profiles', element: lazyPage(() => import('./routes/dashboard/school/profiles/page')) },
              { path: 'enrollments', element: lazyPage(() => import('./routes/dashboard/school/enrollments/page')) },
              { path: 'users', element: lazyPage(() => import('./routes/dashboard/school/users/page')) },
              { path: 'awards', element: lazyPage(() => import('./routes/dashboard/school/awards/page')) },
              { path: 'awards/:teamId', element: lazyPage(() => import('./routes/dashboard/school/awards/$teamId/page')) },
              { path: 'settings', element: lazyPage(() => import('./routes/dashboard/school/settings/page')) },
            ],
          },
          { path: 'institution-no-binding', element: lazyPage(() => import('./routes/dashboard/institution-no-binding').then(m => ({ default: m.InstitutionNoBindingPage }))) },
          {
            path: 'institution',
            element: <InstitutionLayout />,
            children: [
              { index: true, element: lazyPage(() => import('./routes/dashboard/institution/page')) },
              { path: 'profiles', element: lazyPage(() => import('./routes/dashboard/institution/profiles/page')) },
              { path: 'enrollments', element: lazyPage(() => import('./routes/dashboard/institution/enrollments/page')) },
              { path: 'users', element: lazyPage(() => import('./routes/dashboard/institution/users/page')) },
              { path: 'awards', element: lazyPage(() => import('./routes/dashboard/institution/awards/page')) },
              { path: 'awards/:teamId', element: lazyPage(() => import('./routes/dashboard/institution/awards/$teamId/page')) },
              { path: 'settings', element: lazyPage(() => import('./routes/dashboard/institution/settings/page')) },
            ],
          },
          { path: 'users', element: lazyPage(() => import('./routes/dashboard/users').then(m => ({ default: m.UsersPage }))) },
          { path: 'settings', element: lazyPage(() => import('./routes/dashboard/settings/page')) },
          { path: 'preparation', element: lazyPage(() => import('./routes/dashboard/preparation/page')) },
          { path: 'preparation/:eventId', element: lazyPage(() => import('./routes/dashboard/preparation/$eventId/page')) },
          { path: 'my-competitions', element: lazyPage(() => import('./routes/dashboard/my-competitions/page')) },
          { path: 'my-competitions/:teamId', element: lazyPage(() => import('./routes/dashboard/my-competitions/$competitionId/page')) },
          { path: 'enroll/:competitionId', element: lazyPage(() => import('./routes/dashboard/enroll/$competitionId/page')) },
          { path: 'competitions', element: lazyPage(() => import('./routes/dashboard/competitions/page')) },
          { path: 'competitions/:teamId', element: lazyPage(() => import('./routes/dashboard/competitions/$teamId/page')) },
          { path: 'judge', element: lazyPage(() => import('./routes/dashboard/judge').then(m => ({ default: m.JudgePage }))) },
          { path: 'judge/score', element: lazyPage(() => import('./routes/dashboard/judge/score/page')) },
          { path: 'judge/certificates', element: lazyPage(() => import('./routes/dashboard/judge/certificates/page')) },
          { path: 'judge-registration', element: lazyPage(() => import('./routes/dashboard/judge-registration/page')) },
          { path: 'judge-certification', element: lazyPage(() => import('./routes/dashboard/judge-certification/page')) },
          { path: 'judge-certification/exam/:attemptId', element: lazyPage(() => import('./routes/dashboard/judge-certification/exam/$attemptId/page')) },
          { path: 'admin/judges', element: lazyPage(() => import('./routes/dashboard/admin/judges/page')) },
          { path: 'admin/judge-certificates', element: lazyPage(() => import('./routes/dashboard/admin/judge-certificates/page')) },
          { path: 'admin/judge-certificate-batches', element: lazyPage(() => import('./routes/dashboard/admin/judge-certificate-batches/page')) },
          { path: 'admin/judge-certificate-batches/:id', element: lazyPage(() => import('./routes/dashboard/admin/judge-certificate-batches/$id/page')) },
          { path: 'admin/judge-cert/questions', element: lazyPage(() => import('./routes/dashboard/admin/judge-cert/questions/page')) },
          { path: 'admin/judge-cert/configs', element: lazyPage(() => import('./routes/dashboard/admin/judge-cert/configs/page')) },
          { path: 'admin/profiles', element: lazyPage(() => import('./routes/dashboard/admin/profiles/page')) },
          { path: 'admin/profile-issues', element: lazyPage(() => import('./routes/dashboard/admin/profile-issues/page')) },
          { path: 'admin/invite-codes', element: lazyPage(() => import('./routes/dashboard/admin/invite-codes/page')) },
          { path: 'admin/schools', element: lazyPage(() => import('./routes/dashboard/admin/schools/page')) },
          { path: 'admin/schools/:id', element: lazyPage(() => import('./routes/dashboard/admin/schools/$id/page')) },
          { path: 'admin/institutions', element: lazyPage(() => import('./routes/dashboard/admin/institutions/page')) },
          { path: 'admin/institutions/:id', element: lazyPage(() => import('./routes/dashboard/admin/institutions/$id/page')) },
          { path: 'messages', element: lazyPage(() => import('./routes/dashboard/messages')) },
          { path: 'admin/messages', element: lazyPage(() => import('./routes/dashboard/admin/messages')) },
          { path: 'admin/duplicates', element: lazyPage(() => import('./routes/dashboard/admin/duplicates/page')) },
          { path: 'admin/insurances', element: lazyPage(() => import('./routes/dashboard/admin/insurances/page')) },
          { path: 'admin/badge-templates', element: lazyPage(() => import('./routes/dashboard/admin/badge-templates/page')) },
          { path: 'admin/audit-center', element: lazyPage(() => import('./routes/dashboard/audit-center')) },
        ],
      },
    ],
  },
]);

function AppContent() {
  const { locale } = useLocale();
  const [messages, setMessages] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    getMessages(locale).then((msgs) => {
      if (!cancelled) setMessages(msgs);
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  if (!Object.keys(messages).length) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-sky-50 to-cyan-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-card">
              <Loader2 className="w-6 h-6 text-primary-foreground animate-spin" />
            </div>
          </div>
          <p className="text-sm text-muted-foreground font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <IntlProvider locale={locale} messages={messages}>
      <RouterProvider router={router} />
      <EnrollmentConsultSidebar />
    </IntlProvider>
  );
}

export function App() {
  return (
    <HelmetProvider>
      <QueryProvider>
        <ThemeProvider>
          <SystemUiThemeProvider>
            <LocaleProvider>
              <AppContent />
              <Toaster
                position="top-right"
                richColors
                closeButton
                toastOptions={{
                  className: 'sonner-toast glass',
                  classNames: {
                    description: 'text-muted-foreground',
                  },
                }}
              />
            </LocaleProvider>
          </SystemUiThemeProvider>
        </ThemeProvider>
      </QueryProvider>
    </HelmetProvider>
  );
}
